import type { BudgetLineRow } from "@/hooks/use-project-detail";
import type { UserLookup } from "@/hooks/use-users";
import type { LoggedEntryRef } from "@/hooks/use-budget-line-logged-hours";
import { isTaskLine } from "@/lib/billing-calculations";
import { groupLinesByPhase, type PhaseGroupKeyLite } from "@/lib/timetildeling-grouping";
import { BudgetPhaseGroup } from "./BudgetPhaseGroup";
import { TimetildelingSectionBand, TimetildelingRubrikkBand } from "./TimetildelingSectionBand";

interface RubrikkGroup {
  key: string;
  title: string;
  lines: BudgetLineRow[];
}

interface SharedPhaseGroupProps {
  projectId: string;
  users: UserLookup[];
  editingId: string | null;
  setEditingId: (id: string | null) => void;
  readOnly: boolean;
  loggedHoursTotals: Map<string, number>;
  loggedHoursPerUser: Map<string, Map<string, number>>;
  loggedHoursPerAssignment: Map<string, Map<string, number>>;
  entriesPerUser: Map<string, Map<string, LoggedEntryRef[]>>;
  canAssignHours: boolean;
}

interface TimetildelingTableBodyProps extends SharedPhaseGroupProps {
  /** Hovedbudsjett: allerede fase-gruppert og filtrert av kallstedet (samme som før VIB-1230). */
  mainPhaseGroups: Array<{ phase: PhaseGroupKeyLite; lines: BudgetLineRow[] }>;
  /** VIB-1158: én rubrikk per option_label (evt. samlet "Tilvalg"-rubrikk uten label). */
  optionalGroups: RubrikkGroup[];
  /** VIB-1151: én rubrikk per godkjent mersalg som har oppgavelinjer. */
  upsellGroups: RubrikkGroup[];
}

/**
 * VIB-1230: Timetildelingens <tbody> — tre adskilte seksjoner (Hovedtilbud,
 * Tilvalg, Mersalg) med et seksjonsbånd hver, fase-gruppering innen hver
 * tilvalg-/mersalg-rubrikk (samme fase-rekkefølge som hovedbudsjettet), og
 * numrering som kun løper i Hovedtilbud-seksjonen (Tilvalg/Mersalg dropper
 * tallet — rubrikk-tittelen bærer identiteten). Tomme seksjoner rendres ikke.
 *
 * Egen fil fordi BudgetTabContent.tsx var nær 300-linjersgrensen før denne
 * utvidelsen (CLAUDE.md pkt. 1) — denne filen eier KUN rendering, ingen
 * datahenting.
 */
export function TimetildelingTableBody({
  mainPhaseGroups,
  optionalGroups,
  upsellGroups,
  ...phaseGroupProps
}: TimetildelingTableBodyProps) {
  const renderableOptionalGroups = optionalGroups.filter((g) => g.lines.some(isTaskLine));
  const renderableUpsellGroups = upsellGroups.filter((g) => g.lines.some(isTaskLine));

  return (
    <tbody>
      {mainPhaseGroups.length > 0 && (
        <>
          <TimetildelingSectionBand title="Hovedtilbud" />
          {mainPhaseGroups.map(({ phase, lines }, phaseIdx) => (
            <BudgetPhaseGroup
              key={phase}
              phase={phase}
              lines={lines}
              phaseIdx={phaseIdx}
              {...phaseGroupProps}
            />
          ))}
        </>
      )}

      {renderableOptionalGroups.length > 0 && (
        <>
          <TimetildelingSectionBand title="Tilvalg" />
          {renderableOptionalGroups.map((group) => (
            <RubrikkPhaseGroups key={`tilvalg:${group.key}`} group={group} groupPhaseKind="tilvalg" {...phaseGroupProps} />
          ))}
        </>
      )}

      {renderableUpsellGroups.length > 0 && (
        <>
          <TimetildelingSectionBand title="Mersalg" />
          {renderableUpsellGroups.map((group) => (
            <RubrikkPhaseGroups key={`mersalg:${group.key}`} group={group} groupPhaseKind="mersalg" {...phaseGroupProps} />
          ))}
        </>
      )}
    </tbody>
  );
}

/**
 * Én rubrikk (ett tilvalg-option_label eller ett godkjent mersalg): rubrikk-
 * bånd med tittel, deretter linjene fase-sortert (pre → prod → post → reise
 * → annet), én BudgetPhaseGroup per ikke-tom fase med `hideIndex` (dropper
 * nummereringen — rubrikk-tittelen bærer identiteten, ikke et løpende tall).
 */
function RubrikkPhaseGroups({
  group,
  groupPhaseKind,
  ...phaseGroupProps
}: {
  group: RubrikkGroup;
  groupPhaseKind: "tilvalg" | "mersalg";
} & SharedPhaseGroupProps) {
  const phaseGroups = groupLinesByPhase(group.lines).filter((g) => g.lines.some(isTaskLine));

  return (
    <>
      <TimetildelingRubrikkBand title={group.title} />
      {phaseGroups.map(({ phase, lines }) => (
        <BudgetPhaseGroup
          key={`${groupPhaseKind}:${group.key}:${phase}`}
          phase={phase}
          lines={lines}
          phaseIdx={0}
          hideIndex
          {...phaseGroupProps}
        />
      ))}
    </>
  );
}
